import { lazy, Suspense, useEffect } from 'react';
import { ReportDialogHost } from './features/moderation/report';
import PermissionsPage, { PhoneSetupGate } from './mobile/PhoneSetup';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { isPhoneCalView, visitCodeFromPath } from '@tradeoutlet/shared';
import { useClockPref } from '@/lib/clock';
import { useAuth } from './lib/auth';
import { useUi } from './lib/store';
import { Center, NotFound, Spinner, Toaster } from './components/ui';
import { AppShell } from './components/layout/AppShell';
import { SignInPage } from './features/auth/SignInPage';
import { AcceptInvitePage, ForcedPasswordChange, ResetPasswordPage, useSecurityStatus } from './features/auth/password';
import { ForcedMfaEnrol } from './features/auth/mfa';
import { HomePage } from './features/home/HomePage';
import { useMobileLayout } from './mobile/layout';
import { ConversationPage } from './features/chat/ConversationPage';
import { Guard, isExternalDriver } from './lib/navAccess';
import { usePermissions } from './lib/permissions';
import { DriverShell } from './features/fleet/DriverShell';
import { useRunOutboxSync } from './lib/runOutbox';
import { SearchPage } from './features/search/SearchPage';
import { ScreenTips } from './features/tips/ScreenTips';
import { takeRememberedNext } from './lib/signInNext';
import { runLinkToken } from './lib/linkTokens';

// Larger sections are code-split.
const ProjectsPage = lazy(() => import('./features/projects/ProjectsPage'));
const ProjectPage = lazy(() => import('./features/projects/ProjectPage'));
const MyShiftsPage = lazy(() => import('./features/workforce/MyShiftsPage'));
const SchedulePage = lazy(() => import('./features/workforce/SchedulePage'));
const TimeOffPage = lazy(() => import('./features/workforce/TimeOffPage'));
const ImportPage = lazy(() => import('./features/imports/ImportPage'));
const HolidayPlannerPage = lazy(() => import('./features/workforce/holiday/HolidayPlannerPage'));
const TimesheetsPage = lazy(() => import('./features/workforce/TimesheetsPage'));
const ReportsPage = lazy(() => import('./features/workforce/reports/ReportsHub'));
const SkillsPage = lazy(() => import('./features/workforce/reports/SkillsPage'));
const ChecklistsPage = lazy(() => import('./features/ops/ChecklistsPage'));
const ChecklistRunPage = lazy(() => import('./features/ops/ChecklistRunPage'));
const IncidentsPage = lazy(() => import('./features/ops/IncidentsPage'));
const IssueFormPage = lazy(() => import('./features/ops/IssueFormPage'));
const QrScanPage = lazy(() => import('./features/ops/QrScanPage'));
const ScanPage = lazy(() => import('./features/ops/ScanPage'));
const EquipmentPage = lazy(() => import('./features/ops/EquipmentPage'));
const EquipmentDetailPage = lazy(() => import('./features/ops/EquipmentPage').then(m => ({ default: m.EquipmentDetailPage })));
const MobileEquipmentPage = lazy(() => import('./mobile/EquipmentPage'));
const ChecklistTemplateBuilderPage = lazy(() => import('./features/ops/ChecklistTemplateBuilder').then(m => ({ default: m.ChecklistTemplateBuilderPage })));
const MobileEquipmentDetail = lazy(() => import('./mobile/EquipmentPage').then(m => ({ default: m.MobileEquipmentDetail })));
const VisitorsPage = lazy(() => import('./features/workforce/visitors/VisitorsPage'));
const MobileVisitorsPage = lazy(() => import('./mobile/VisitorsPage'));
const DashboardPage = lazy(() => import('./features/workforce/dashboard/DashboardPage'));
const MobileDashboardPage = lazy(() => import('./mobile/DashboardPage'));
const StoreSalesPage = lazy(() => import('./features/sales/StoreSalesPage'));
const TrainingPage = lazy(() => import('./features/knowledge/training/TrainingPages'));
const TrainingCoursePage = lazy(() => import('./features/knowledge/training/TrainingPages').then(m => ({ default: m.TrainingCoursePage })));
const PoliciesPage = lazy(() => import('./features/policies/PoliciesPage'));
const PolicyApprovalsPage = lazy(() => import('./features/policies/PoliciesPage').then(m => ({ default: m.PolicyApprovalsPage })));
const PolicyPage = lazy(() => import('./features/policies/PolicyPage'));
const PolicyEditorPage = lazy(() => import('./features/policies/PolicyEditor'));
const PolicyComparePage = lazy(() => import('./features/policies/ComparePage'));
const MobilePoliciesPage = lazy(() => import('./mobile/PoliciesPage'));
const MobilePolicyPage = lazy(() => import('./mobile/PoliciesPage').then(m => ({ default: m.MobilePolicyPage })));
const MobileTrainingPage = lazy(() => import('./mobile/TrainingPage'));
const MobileTrainingCoursePage = lazy(() => import('./mobile/TrainingPage').then(m => ({ default: m.MobileTrainingCoursePage })));
const IncidentPage = lazy(() => import('./features/ops/IncidentPage'));
const AnnouncementsPage = lazy(() => import('./features/knowledge/AnnouncementsPage'));
const DigestsPage = lazy(() => import('./features/ai/DigestsPage'));
const DigestPage = lazy(() => import('./features/ai/DigestsPage').then(m => ({ default: m.DigestPage })));
const AnnouncementPage = lazy(() => import('./features/knowledge/AnnouncementsPage').then(m => ({ default: m.AnnouncementPage })));
const KbPage = lazy(() => import('./features/knowledge/KbPage'));
const ArticlePage = lazy(() => import('./features/knowledge/ArticlePage'));
const ArticleEditorPage = lazy(() => import('./features/knowledge/ArticlePage').then(m => ({ default: m.ArticleEditorPage })));
const PolicyCompliancePage = lazy(() => import('./features/knowledge/ArticlePage').then(m => ({ default: m.PolicyCompliancePage })));
const OnboardingPage = lazy(() => import('./features/knowledge/OnboardingPages'));
const OnboardingDetailPage = lazy(() => import('./features/knowledge/OnboardingPages').then(m => ({ default: m.OnboardingDetailPage })));
const HandoversPage = lazy(() => import('./features/workforce/handovers/HandoversPage'));
const ChatListPage = lazy(() => import('./features/chat/ChatListPage'));
const MobileChatPage = lazy(() => import('./mobile/ChatPage'));
const BrowseChannelsPage = lazy(() => import('./features/chat/BrowseChannelsPage'));
const ClosedRoomsPage = lazy(() => import('./features/rooms/ClosedRoomsPage'));
const ScheduledMessagesPage = lazy(() => import('./features/chat/scheduled/ScheduledMessagesPage'));
const MyTasksPage = lazy(() => import('./features/tasks/MyTasksPage'));
const SettingsHub = lazy(() => import('./features/settings/SettingsHub'));
const LegacySettingsRedirect = lazy(() => import('./features/settings/SettingsHub').then(m => ({ default: m.LegacySettingsRedirect })));
const GroupsPage = lazy(() => import('./features/people/GroupsPage'));
const PeoplePage = lazy(() => import('./features/people/PeoplePage'));
const BuildingsPage = lazy(() => import('./features/buildings/BuildingsPage'));
const BuildingPage = lazy(() => import('./features/buildings/BuildingPage'));
const MobileBuildingsPage = lazy(() => import('./mobile/buildings/BuildingsPage'));
const MobilePeoplePage = lazy(() => import('./mobile/people/PeoplePage'));
const MobileBuildingOverview = lazy(() => import('./mobile/buildings/BuildingOverview'));
const MobileBuildingIssues = lazy(() => import('./mobile/buildings/BuildingIssues'));
const AcksPage = lazy(() => import('./features/acks/AcksPage'));
const CalendarPage = lazy(() => import('./features/calendar/CalendarPage'));
const KudosPage = lazy(() => import('./features/people/KudosPage'));
const AssistantPage = lazy(() => import('./features/assistant/Assistant'));
const KioskPage = lazy(() => import('./features/kiosk/KioskPage'));
const ReceptionPage = lazy(() => import('./features/visit/ReceptionPage')); // t627u batch C
const FormsListPage = lazy(() => import('./features/forms/FormsPage').then(m => ({ default: m.FormsListPage })));
const FleetPage = lazy(() => import('./features/fleet/FleetPage'));
const FleetRedirect = lazy(() => import('./features/fleet/FleetPage').then(m => ({ default: m.FleetRedirect })));
const JourneyPage = lazy(() => import('./features/fleet/JourneyPage'));
const LiveMapPage = lazy(() => import('./features/fleet/LiveMapPage'));
const FleetDashboardPage = lazy(() => import('./features/fleet/FleetDashboardPage'));
const VehiclePage = lazy(() => import('./features/fleet/VehiclePage'));
const MobileVehiclesPage = lazy(() => import('./mobile/FleetPages'));
const MobileVehicleDetail = lazy(() => import('./mobile/FleetPages').then(m => ({ default: m.MobileVehicleDetail })));
const MobileJourneysPage = lazy(() => import('./mobile/FleetPages').then(m => ({ default: m.MobileJourneysPage })));
const MobileJourneyDetail = lazy(() => import('./mobile/FleetPages').then(m => ({ default: m.MobileJourneyDetail })));
const MyRunsPage = lazy(() => import('./features/fleet/DriverPages').then(m => ({ default: m.MyRunsPage })));
const RunPage = lazy(() => import('./features/fleet/DriverPages').then(m => ({ default: m.RunPage })));
const PublicRunPage = lazy(() => import('./features/fleet/DriverPages').then(m => ({ default: m.PublicRunPage })));
const WalkaroundPage = lazy(() => import('./features/fleet/DriverPages').then(m => ({ default: m.WalkaroundPage })));
const MobileRunsPage = lazy(() => import('./mobile/RunsPage'));
const MobileRunPage = lazy(() => import('./mobile/RunsPage').then(m => ({ default: m.MobileRunPage })));
const DefectPage = lazy(() => import('./features/fleet/DriverPages').then(m => ({ default: m.DefectPage })));
const MobileWalkaroundPage = lazy(() => import('./mobile/VehicleCheck').then(m => ({ default: m.MobileWalkaroundPage })));
const MobileDefectPage = lazy(() => import('./mobile/VehicleCheck').then(m => ({ default: m.MobileDefectPage })));
const HelpCentre = lazy(() => import('./features/help/HelpCentre'));
const MePage = lazy(() => import('./mobile/MePage'));

const TodayPage = lazy(() => import('./mobile/TodayPage'));
const ShiftsPage = lazy(() => import('./mobile/ShiftsPage'));
const ShiftDetailPage = lazy(() => import('./mobile/ShiftDetailPage'));
const FeedPage = lazy(() => import('./mobile/FeedPage'));
const MobileTasksPage = lazy(() => import('./mobile/TasksPage'));
const MobileIssuesPage = lazy(() => import('./mobile/IssuesPage'));
const MobileIssuePage = lazy(() => import('./mobile/IssuesPage').then(m => ({ default: m.MobileIssuePage })));
const MobileChecklistsPage = lazy(() => import('./mobile/ChecklistsPage'));
const MobileKudosPage = lazy(() => import('./mobile/ItemDetail').then(m => ({ default: m.MobileKudosPage })));
const MobileCalendarPage = lazy(() => import('./mobile/CalendarAgenda').then(m => ({ default: m.MobileCalendarPage })));
const AckRoute = lazy(() => import('./mobile/AckStatusPage').then(m => ({ default: m.AckRoute })));
const MobileEventPage = lazy(() => import('./mobile/ItemDetail').then(m => ({ default: m.MobileEventPage })));
const MobileOfferPage = lazy(() => import('./mobile/ItemDetail').then(m => ({ default: m.MobileOfferPage })));
const MobileChecklistRun = lazy(() => import('./mobile/ChecklistsPage').then(m => ({ default: m.MobileChecklistRun })));
const MobileAnnouncementsPage = lazy(() => import('./mobile/AnnouncementsPage'));
const MobileAnnouncementPage = lazy(() => import('./mobile/AnnouncementsPage').then(m => ({ default: m.MobileAnnouncementPage })));
const NotificationsPage = lazy(() => import('./mobile/NotificationsPage'));
const MobileRotaPage = lazy(() => import('./mobile/RotaPage'));
const MobileApprovalsPage = lazy(() => import('./mobile/ApprovalsPage'));
const MobileKbPage = lazy(() => import('./mobile/HelpKbAcks').then(m => ({ default: m.MobileKbPage })));
const MobileAcksPage = lazy(() => import('./mobile/HelpKbAcks').then(m => ({ default: m.MobileAcksPage })));
const FormBuilderPage = lazy(() => import('./features/forms/FormsPage').then(m => ({ default: m.FormBuilderPage })));

export function App() {
  const { status, userId } = useAuth();
  useRunOutboxSync(status === 'signed_in' ? userId : null);
  const security = useSecurityStatus(status === 'signed_in');
  const perms = usePermissions();
  const theme = useUi(s => s.theme);
  const loc = useLocation();
  // 12 / 24-hour clock: changing it re-renders the whole app so every time picks it up.
  const clockPref = useClockPref();
  useEffect(() => { document.documentElement.classList.toggle('dark', theme === 'dark'); }, [theme]);
  // t449u: back to the emailed link after Microsoft sign-in (which returns to the home page).
  const navigate = useNavigate();
  useEffect(() => {
    if (status !== 'signed_in') return;
    const next = takeRememberedNext();
    if (next && next !== loc.pathname + loc.search) navigate(next, { replace: true });
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shared clock-in tablets use a device token, never a person's session (backlog #26).
  if (loc.pathname === '/kiosk') return <Lazy><KioskPage /></Lazy>;
  // t627u batch C: visitor sign-in on the visitor's own phone (public, signed in or not).
  const visitCode = visitCodeFromPath(loc.pathname);
  if (visitCode) return <><Lazy><ReceptionPage code={visitCode} /></Lazy><Toaster /></>;
  // Emailed reset links land here signed out or with a fresh recovery session; it manages both.
  // Self-employed drivers' one-job magic links: no login, outside the app shell.
  // Driver magic links: /run#<token> (t173, the token never reaches a server) or the older /run/<token>.
  const runToken = runLinkToken(loc.pathname, loc.hash);
  if (runToken) return <><Lazy><PublicRunPage token={runToken} /></Lazy><Toaster /></>;
  if (loc.pathname === '/reset-password') return <><ResetPasswordPage /><Toaster /></>;
  // Invite emails: choose a password, then straight into the Hub (the API applies the invitation on first sign-in).
  if (loc.pathname === '/accept-invite') return <><AcceptInvitePage /><Toaster /></>;
  if (status === 'loading') return <Center><Spinner /></Center>;
  // t413u: 'mfa' (password done, two-factor code to enter) stays on Sign in, which shows the code step.
  if (status === 'signed_out' || status === 'mfa') {
    return (
      <>
        <Routes>
          <Route path="/signin" element={<SignInPage />} />
          <Route path="*" element={<Navigate to={`/signin?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />} />
        </Routes>
        <Toaster />
      </>
    );
  }
  if (security.data?.must_change_password) return <><ForcedPasswordChange /><Toaster /></>;
  if (security.data?.mfa?.enrolment_required) return <><ForcedMfaEnrol /><Toaster /></>;
  if (perms.isLoading && perms.fetchStatus !== 'idle') return <Center><Spinner /></Center>;
  if (isExternalDriver(perms.data)) {
    // Limited login for self-employed drivers: their runs and vehicle checks only (the API enforces the same).
    return (
      <>
        <Routes>
          <Route element={<DriverShell />}>
            <Route path="fleet/my-runs" element={<MyRunsRoute />} />
            <Route path="fleet/run/:id" element={<RunRoute />} />
            <Route path="fleet/check" element={<CheckRoute />} />
            <Route path="fleet/report" element={<ReportRoute />} />
            <Route path="*" element={<Navigate to="/fleet/my-runs" replace />} />
          </Route>
        </Routes>
        <Toaster />
      </>
    );
  }
  return (
    <>
      <Routes key={clockPref}>
        <Route path="/signin" element={<Navigate to="/" replace />} />
        <Route element={<AppShell />}>
          <Route index element={<HomeOrToday />} />
          <Route path="c/:id" element={<ConversationPage />} />
          <Route path="search" element={<SearchPage />} />
          {/* Phase 0 phone screens (they work at any width). */}
          <Route path="me" element={<Lazy><MePage /></Lazy>} />
          <Route path="permissions" element={<PermissionsPage />} />
          <Route path="feed" element={<Lazy><FeedPage /></Lazy>} />
          <Route path="today" element={<TodayRoute />} />
          <Route path="rota" element={<RotaRoute />} />
          <Route path="rota/approvals" element={<ApprovalsRoute />} />
          <Route path="notifications" element={<Lazy><NotificationsPage /></Lazy>} />
          <Route path="chat" element={<Lazy><ChatRoute /></Lazy>} />
          <Route path="chat/scheduled" element={<Lazy><ScheduledMessagesPage /></Lazy>} />
          <Route path="channels" element={<Lazy><BrowseChannelsPage /></Lazy>} />
          <Route path="meeting-rooms/closed" element={<Lazy><ClosedRoomsPage /></Lazy>} />
          {/* Rooms were renamed ad-hoc meeting rooms (#54): old links still work. */}
          <Route path="rooms/closed" element={<MovedTo path="/meeting-rooms/closed" />} />
          <Route path="tasks" element={<Lazy><TasksRoute /></Lazy>} />
          <Route path="tasks/checklists" element={<Lazy><TasksRoute /></Lazy>} />
          <Route path="projects" element={<Lazy><ProjectsPage /></Lazy>} />
          <Route path="projects/:id" element={<Lazy><ProjectPage /></Lazy>} />
          <Route path="workforce" element={<Navigate to="/workforce/my-shifts" replace />} />
          <Route path="workforce/my-shifts" element={<Lazy><MyShiftsRoute /></Lazy>} />
          <Route path="workforce/my-shifts/:id" element={<Lazy><ShiftDetailPage /></Lazy>} />
          <Route path="workforce/offers/:id" element={<Lazy><OfferRoute /></Lazy>} />
          <Route path="workforce/schedule" element={<Guard path="/workforce/schedule"><Lazy><SchedulePage /></Lazy></Guard>} />
          <Route path="workforce/time-off" element={<Lazy><TimeOffPage /></Lazy>} />
          <Route path="workforce/import/:kind" element={<Lazy><ImportPage /></Lazy>} />
          <Route path="workforce/time-off/planner" element={<Lazy><HolidayPlannerPage /></Lazy>} />
          <Route path="workforce/timesheets" element={<Lazy><TimesheetsPage /></Lazy>} />
          <Route path="workforce/reports" element={<Guard path="/workforce/reports"><Lazy><ReportsPage /></Lazy></Guard>} />
          <Route path="workforce/reports/:key" element={<Guard path="/workforce/reports"><Lazy><ReportsPage /></Lazy></Guard>} />
          <Route path="workforce/skills" element={<Guard path="/workforce/skills"><Lazy><SkillsPage /></Lazy></Guard>} />
          <Route path="checklists" element={<Lazy><ChecklistsRoute /></Lazy>} />
          <Route path="checklists/run/:id" element={<Lazy><ChecklistRunRoute /></Lazy>} />
          <Route path="checklists/templates/:id" element={<Lazy><ChecklistTemplateRoute /></Lazy>} />
          <Route path="incidents" element={<Lazy><IncidentsRoute /></Lazy>} />
          <Route path="incidents/form" element={<Lazy><IssueFormPage /></Lazy>} />
          <Route path="incidents/:id" element={<Lazy><IncidentRoute /></Lazy>} />
          <Route path="equipment" element={<Guard path="/equipment"><Lazy><EquipmentRoute /></Lazy></Guard>} />
          <Route path="equipment/:id" element={<Guard path="/equipment"><Lazy><EquipmentItemRoute /></Lazy></Guard>} />
          <Route path="visitors" element={<Guard path="/visitors"><Lazy><VisitorsRoute /></Lazy></Guard>} />
          <Route path="dashboard" element={<DashboardGate><Lazy><DashboardRoute /></Lazy></DashboardGate>} />
          <Route path="sales" element={<Guard path="/sales"><Lazy><SalesRoute /></Lazy></Guard>} />
          <Route path="training" element={<Lazy><TrainingRoute /></Lazy>} />
          <Route path="training/:id" element={<Lazy><TrainingCourseRoute /></Lazy>} />
          <Route path="policies" element={<Lazy><PoliciesRoute /></Lazy>} />
          <Route path="policies/approvals" element={<Lazy><PoliciesRoute approvals /></Lazy>} />
          <Route path="policies/:id" element={<Lazy><PolicyRoute /></Lazy>} />
          <Route path="policies/:id/edit" element={<Lazy><PolicyEditorRoute /></Lazy>} />
          <Route path="policies/:id/compare" element={<Lazy><PolicyComparePage /></Lazy>} />
          <Route path="qr/:code" element={<Lazy><QrScanPage /></Lazy>} />
          <Route path="scan" element={<Lazy><ScanPage /></Lazy>} />
          <Route path="announcements" element={<Lazy><AnnouncementsRoute /></Lazy>} />
          <Route path="announcements/:id" element={<Lazy><AnnouncementRoute /></Lazy>} />
          <Route path="digests" element={<Lazy><DigestsPage /></Lazy>} />
          <Route path="digests/:id" element={<Lazy><DigestPage /></Lazy>} />
          {/* #61: the knowledge base lives in Help & knowledge base; /kb (with ?collection=, ?q=) moves there. Article URLs stay. */}
          <Route path="kb" element={<MovedTo path="/help/kb" />} />
          <Route path="help/kb" element={<Lazy><KbRoute /></Lazy>} />
          <Route path="kb/new" element={<Lazy><ArticleEditorPage /></Lazy>} />
          <Route path="kb/:id" element={<Lazy><ArticlePage /></Lazy>} />
          <Route path="kb/:id/edit" element={<Lazy><ArticleEditorPage /></Lazy>} />
          <Route path="kb/:id/compliance" element={<Lazy><PolicyCompliancePage /></Lazy>} />
          <Route path="onboarding" element={<Lazy><OnboardingPage /></Lazy>} />
          <Route path="onboarding/:id" element={<Lazy><OnboardingDetailPage /></Lazy>} />
          <Route path="workforce/handovers" element={<Guard path="/workforce/handovers"><Lazy><HandoversPage /></Lazy></Guard>} />
          {/* t697u: handover templates are a tab on Handovers now; old links still work. */}
          <Route path="workforce/handover-templates" element={<Navigate to="/workforce/handovers?tab=templates" replace />} />
          <Route path="workforce/handovers/new" element={<Navigate to="/workforce/handovers?new=1" replace />} />
          {/* UI review: one Settings hub; the old Admin and Workforce setup URLs redirect into it. */}
          <Route path="workforce/settings" element={<Lazy><LegacySettingsRedirect /></Lazy>} />
          <Route path="admin" element={<Lazy><LegacySettingsRedirect /></Lazy>} />
          <Route path="settings" element={<Lazy><SettingsHub /></Lazy>} />
          {/* t373u: the expiry reminder link; phones show their own page (MobileShell) before this. t613u: HR records are in Users now. */}
          <Route path="settings/people/hr-expiring" element={<Navigate to="/settings/people/users?tab=expiring" replace />} />
          {/* t413u: the Today strip, Home widget and reminder links; phones show their own pages first. */}
          <Route path="settings/people/hr-changes" element={<Navigate to="/settings/people/users?tab=changes" replace />} />
          <Route path="settings/people/hr-reports" element={<Navigate to="/settings/people/users?tab=reports" replace />} />
          <Route path="settings/people/hr-missing" element={<Navigate to="/settings/people/users?tab=missing" replace />} />
          <Route path="settings/:group/:item" element={<Lazy><SettingsHub /></Lazy>} />
          <Route path="settings/:group/:item/:sub" element={<Lazy><SettingsHub /></Lazy>} />
          <Route path="groups" element={<Lazy><GroupsPage /></Lazy>} />
          <Route path="departments" element={<Lazy><GroupsPage key="department" only="department" /></Lazy>} />
          <Route path="custom-groups" element={<Lazy><GroupsPage key="group" only="group" /></Lazy>} />
          <Route path="people" element={<Lazy><PeopleRoute /></Lazy>} />
          <Route path="buildings" element={<Lazy><BuildingsRoute /></Lazy>} />
          <Route path="buildings/:id" element={<Lazy><BuildingRoute /></Lazy>} />
          <Route path="buildings/:id/issues" element={<Lazy><BuildingIssuesRoute /></Lazy>} />
          <Route path="acknowledgements" element={<Lazy><AcksRoute /></Lazy>} />
          <Route path="acknowledgements/:messageId" element={<Lazy><AckRoute /></Lazy>} />
          <Route path="calendar" element={<Lazy><CalendarRoute /></Lazy>} />
          <Route path="calendar/events/:id" element={<Lazy><EventRoute /></Lazy>} />
          {/* My to-dos merged into Tasks as private tasks. */}
          <Route path="todos" element={<Navigate to="/tasks?visibility=private" replace />} />
          <Route path="kudos" element={<Lazy><KudosPage /></Lazy>} />
          <Route path="kudos/:id" element={<Lazy><KudosPostRoute /></Lazy>} />
          <Route path="assistant" element={<Guard path="/assistant"><Lazy><AssistantPage /></Lazy></Guard>} />
          <Route path="fleet" element={<Guard path="/fleet"><Lazy><FleetRedirect /></Lazy></Guard>} />
          {(['calendar', 'drivers', 'hauliers', 'reports'] as const).map(t => (
            <Route key={t} path={`fleet/${t}`} element={<Guard path={`/fleet/${t}`}><Lazy><FleetPage section={t} /></Lazy></Guard>} />
          ))}
          <Route path="fleet/journeys" element={<Guard path="/fleet/journeys"><Lazy><JourneysRoute /></Lazy></Guard>} />
          <Route path="fleet/dashboard" element={<Guard path="/fleet/dashboard"><Lazy><FleetDashboardPage /></Lazy></Guard>} />
          <Route path="fleet/live" element={<Guard path="/fleet/live"><Lazy><LiveMapPage /></Lazy></Guard>} />
          <Route path="fleet/vehicles" element={<Guard path="/fleet/vehicles"><Lazy><VehiclesRoute /></Lazy></Guard>} />
          <Route path="fleet/journeys/:id" element={<Guard path="/fleet/journeys"><Lazy><JourneyRoute /></Lazy></Guard>} />
          <Route path="fleet/vehicles/:id" element={<Guard path="/fleet/vehicles"><Lazy><VehicleRoute /></Lazy></Guard>} />
          <Route path="fleet/my-runs" element={<Guard path="/fleet/my-runs"><MyRunsRoute /></Guard>} />
          <Route path="fleet/run/:id" element={<RunRoute />} />
          <Route path="fleet/check" element={<Guard path="/fleet/my-runs"><CheckRoute /></Guard>} />
          <Route path="fleet/report" element={<Guard path="/fleet/my-runs"><ReportRoute /></Guard>} />
          <Route path="forms" element={<Guard path="/forms"><Lazy><FormsListPage /></Lazy></Guard>} />
          <Route path="help/*" element={<Lazy><HelpCentre /></Lazy>} />
          <Route path="forms/:id" element={<Guard path="/forms"><Lazy><FormBuilderPage /></Lazy></Guard>} />
          <Route path="*" element={<PageNotFound />} />
        </Route>
      </Routes>
      <ScreenTips />
      {/* t428u: "Set up your phone" after the first sign-in in this browser. */}
      <PhoneSetupGate />
      <Toaster />
      <ReportDialogHost />
    </>
  );
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Center><Spinner /></Center>}>{children}</Suspense>;
}

/** A page that moved: keep the search and filters (?q=…) when redirecting. */
function MovedTo({ path }: { path: string }) {
  const loc = useLocation();
  return <Navigate to={`${path}${loc.search}${loc.hash}`} replace />;
}

/** Phone layout: one chat list by latest activity (mobile Phase 1); desktop keeps sectioned lists. */
function ChatRoute() {
  return useMobileLayout() ? <MobileChatPage /> : <ChatListPage />;
}

/** Phone layout: announcements with the Acknowledge bar (mobile Phase 2); Manage (?tab=manage) stays the full page. */
function AnnouncementsRoute() {
  const [params] = useSearchParams();
  return useMobileLayout() && params.get('tab') !== 'manage' ? <MobileAnnouncementsPage /> : <AnnouncementsPage />;
}
/** Phone layout: My tasks | My checklists (t214u, /tasks/checklists); ?full=1, filters (?project=, ?scope=…) and desktop: the full page. */
/** t344u #3: the phone gets the app's lean knowledge base and acknowledgements screens. */
function KbRoute() { return useMobileLayout() ? <MobileKbPage /> : <KbPage />; }
function AcksRoute() { return useMobileLayout() ? <MobileAcksPage /> : <AcksPage />; }
/** t209u: a feed card's own item on a phone; the desktop keeps its page with that item opened or highlighted. */
function KudosPostRoute() {
  const { id = '' } = useParams();
  return useMobileLayout() ? <MobileKudosPage /> : <Navigate to={`/kudos?post=${id}`} replace />;
}
/** t213u: the phone calendar is a month agenda; ?event= opens the event page. t335u: ?view=agenda|day|week|month opens
 * that phone view; any other ?view keeps the full calendar. */
function CalendarRoute() {
  const [params] = useSearchParams();
  const phone = useMobileLayout();
  const ev = params.get('event');
  if (phone && ev) return <Navigate to={`/calendar/events/${ev}`} replace />;
  return phone && (!params.has('view') || isPhoneCalView(params.get('view'))) ? <MobileCalendarPage /> : <CalendarPage />;
}
function EventRoute() {
  const { id = '' } = useParams();
  return useMobileLayout() ? <MobileEventPage /> : <Navigate to={`/calendar?event=${id}`} replace />;
}
function OfferRoute() {
  return useMobileLayout() ? <MobileOfferPage /> : <Navigate to="/workforce/my-shifts?tab=offers" replace />;
}
function TasksRoute() {
  const [params] = useSearchParams();
  const filtered = ['full', 'project', 'scope', 'status', 'location_id', 'visibility', 'due'].some(k => params.has(k));
  const mobile = useMobileLayout();
  const loc = useLocation();
  if (!mobile && loc.pathname.startsWith('/tasks/checklists')) return <Navigate to="/checklists" replace />;
  return mobile && !filtered ? <MobileTasksPage /> : <MyTasksPage />;
}
/** Phone issues and checklists (Phase 2, chunk 3); ?full=1 or a desktop tab (reports, settings) keeps the full page. */
function IncidentsRoute() {
  const [params] = useSearchParams();
  const full = params.has('full') || (params.has('tab') && params.get('tab') !== 'tickets');
  return useMobileLayout() && !full ? <MobileIssuesPage /> : <IncidentsPage />;
}
function IncidentRoute() {
  return useMobileLayout() ? <MobileIssuePage /> : <IncidentPage />;
}
/** t335u: the equipment register (phone list/detail with sheets; desktop table/detail with modals). */
function EquipmentRoute() {
  return useMobileLayout() ? <MobileEquipmentPage /> : <EquipmentPage />;
}
function EquipmentItemRoute() {
  return useMobileLayout() ? <MobileEquipmentDetail /> : <EquipmentDetailPage />;
}
/** t335u: visitors, on site now and the fire roll call. */
function VisitorsRoute() {
  return useMobileLayout() ? <MobileVisitorsPage /> : <VisitorsPage />;
}
/** t335u: the manager dashboard. */
function DashboardRoute() {
  return useMobileLayout() ? <MobileDashboardPage /> : <DashboardPage />;
}
/** t554u: the phone Dashboard also holds Store sales, so sales.view alone opens it there. */
function DashboardGate({ children }: { children: React.ReactNode }) {
  return <Guard path="/dashboard" also={useMobileLayout() ? '/sales' : undefined}>{children}</Guard>;
}
/** t554u: Store sales; on a phone it's the Store sales card on the Dashboard. */
function SalesRoute() {
  return useMobileLayout() ? <Navigate to="/dashboard" replace /> : <StoreSalesPage />;
}
/** t563u: policies. Phones read, acknowledge and approve; writing is desktop only. */
function PoliciesRoute({ approvals }: { approvals?: boolean }) {
  if (useMobileLayout()) return <MobilePoliciesPage approvals={approvals} />;
  return approvals ? <PolicyApprovalsPage /> : <PoliciesPage />;
}
function PolicyRoute() {
  return useMobileLayout() ? <MobilePolicyPage /> : <PolicyPage />;
}
function PolicyEditorRoute() {
  const { id = '' } = useParams();
  return useMobileLayout() ? <Navigate to={`/policies/${id}`} replace /> : <PolicyEditorPage />;
}

/** t335u: training courses (articles + quiz). */
function TrainingRoute() {
  return useMobileLayout() ? <MobileTrainingPage /> : <TrainingPage />;
}
function TrainingCourseRoute() {
  return useMobileLayout() ? <MobileTrainingCoursePage /> : <TrainingCoursePage />;
}
function ChecklistsRoute() {
  const [params] = useSearchParams();
  // t533u: ?tab=templates (Settings' way back) opens the phone page's Templates sheet.
  const setup = params.get('tab') === 'templates' || params.get('tab') === 'schedules';
  return useMobileLayout() && !params.has('full') && (!params.has('tab') || setup) ? <MobileChecklistsPage /> : <ChecklistsPage />;
}
/** t721u: make or change a checklist template in the shared template builder (computer and phone). */
function ChecklistTemplateRoute() {
  return <ChecklistTemplateBuilderPage />;
}
function ChecklistRunRoute() {
  return useMobileLayout() ? <MobileChecklistRun /> : <ChecklistRunPage />;
}
/** Phone Runs tab (Phase 3, chunk 1): today's run with a map, stops, proof of delivery, all through the offline outbox. */
// t443u: the phone has its own Vehicles and Journeys pages (⋯ More); /fleet/journeys/new opens Plan a journey over the list.
function VehiclesRoute() {
  return useMobileLayout() ? <MobileVehiclesPage /> : <FleetPage section="vehicles" />;
}
function VehicleRoute() {
  return useMobileLayout() ? <MobileVehicleDetail /> : <VehiclePage />;
}
function JourneysRoute() {
  return useMobileLayout() ? <MobileJourneysPage /> : <FleetPage section="journeys" />;
}
function JourneyRoute() {
  const { id } = useParams();
  if (!useMobileLayout()) return <JourneyPage />;
  return id === 'new' ? <MobileJourneysPage startAdding /> : <MobileJourneyDetail />;
}
function MyRunsRoute() {
  return <Lazy>{useMobileLayout() ? <MobileRunsPage /> : <MyRunsPage />}</Lazy>;
}
function RunRoute() {
  return <Lazy>{useMobileLayout() ? <MobileRunPage /> : <RunPage />}</Lazy>;
}
/** Phone vehicle check and defect report (Phase 3, chunk 2): big OK / Fault rows, camera-first defects, offline outbox. */
function CheckRoute() {
  return <Lazy>{useMobileLayout() ? <MobileWalkaroundPage /> : <WalkaroundPage />}</Lazy>;
}
function ReportRoute() {
  return <Lazy>{useMobileLayout() ? <MobileDefectPage /> : <DefectPage />}</Lazy>;
}
/** t220u: phone Buildings list, overview and building issues; ?view= / ?tab= (archived, settings, access) and desktop keep the full pages. */
/** t231u: the phone People list; ?view keeps the desktop page. */
function PeopleRoute() {
  const [params] = useSearchParams();
  return useMobileLayout() && !params.has('view') ? <MobilePeoplePage /> : <PeoplePage />;
}
function BuildingsRoute() {
  const [params] = useSearchParams();
  return useMobileLayout() && !params.has('view') ? <MobileBuildingsPage /> : <BuildingsPage />;
}
function BuildingRoute() {
  const [params] = useSearchParams();
  return useMobileLayout() && !params.has('tab') ? <MobileBuildingOverview /> : <BuildingPage />;
}
function BuildingIssuesRoute() {
  const { id = '' } = useParams();
  return useMobileLayout() ? <MobileBuildingIssues /> : <Navigate to={`/incidents?location_id=${id}`} replace />;
}
function AnnouncementRoute() {
  return useMobileLayout() ? <MobileAnnouncementPage /> : <AnnouncementPage />;
}

/** Phone layout (t182u): Home is the feed with the Today strip on top; desktop: Home with its widgets. */
function HomeOrToday() {
  return useMobileLayout() ? <Lazy><FeedPage home /></Lazy> : <HomePage />;
}
/** The manager's phone Rota tab (t182u); on a computer the schedule. */
/** t182u: the phone approvals inbox; on a computer the desktop timesheets approvals tab. */
function ApprovalsRoute() {
  return useMobileLayout() ? <Lazy><MobileApprovalsPage /></Lazy> : <Navigate to="/workforce/timesheets?tab=approvals" replace />;
}
function RotaRoute() {
  return useMobileLayout() ? <Lazy><MobileRotaPage /></Lazy> : <Navigate to="/workforce/schedule" replace />;
}
/** The full Today screen, one tap from the strip (phone only; a computer goes Home). */
function TodayRoute() {
  return useMobileLayout() ? <Lazy><TodayPage /></Lazy> : <Navigate to="/" replace />;
}

/** Phone layout: the Shifts agenda (mobile Phase 1); Team and Availability (?full=1) and desktop: the full page. */
function MyShiftsRoute() {
  const [params] = useSearchParams();
  return useMobileLayout() && params.get('full') !== '1' ? <ShiftsPage /> : <MyShiftsPage />;
}

/** t338u: ⌘K means nothing on a phone, so the phone copy points at the menu and search instead (matches the app). */
function PageNotFound() {
  const phone = useMobileLayout();
  return <NotFound title="Page not found">{phone ? 'The link may be old or mistyped. Use the menu or search to find what you need.' : 'The link may be old or mistyped. Use the menu, ⌘K or the Help Centre to find what you need.'}</NotFound>;
}
